import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ArrowRight, Plus } from "lucide-react";
import PageHero from "@/components/PageHero";
import SectionHeading from "@/components/SectionHeading";
import CtaBand from "@/components/CtaBand";
import { Reveal } from "@/components/Reveal";
import { services, processSteps, heroImages } from "@/data/content";

const ServiceCard = ({ service, open, onToggle, index }) => (
  <Reveal delay={(index % 3) * 0.1} className="h-full">
    <div
      data-testid={`service-card-${service.id}`}
      onClick={onToggle}
      className={`group flex h-full cursor-pointer flex-col rounded-3xl border bg-white p-8 transition-all duration-500 ${
        open
          ? "border-gold/60 shadow-2xl shadow-navy/10"
          : "border-navy/10 hover:-translate-y-2 hover:border-gold/40 hover:shadow-xl hover:shadow-navy/10"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <span className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl transition-all duration-500 ${open ? "bg-gold text-navy" : "bg-navy text-gold group-hover:bg-gold group-hover:text-navy"}`}>
          <service.icon size={24} strokeWidth={1.8} />
        </span>
        <span className={`flex h-9 w-9 items-center justify-center rounded-full border transition-all duration-500 ${open ? "rotate-45 border-gold bg-gold text-navy" : "border-navy/15 text-navy/40 group-hover:border-gold group-hover:text-gold-dark"}`}>
          <Plus size={17} />
        </span>
      </div>
      <h3 className="mt-6 text-xl font-bold text-navy">{service.title}</h3>
      <p className="mt-3 text-sm leading-relaxed text-navy/60">{service.short}</p>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="overflow-hidden"
          >
            <div className="mt-7 border-t border-navy/10 pt-7" onClick={(e) => e.stopPropagation()}>
              <p className="text-sm leading-relaxed text-navy/70">{service.description}</p>

              <div className="mt-7 grid gap-8 sm:grid-cols-2">
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-dark">Vantaggi</h4>
                  <ul className="mt-4 space-y-3">
                    {service.vantaggi.map((v) => (
                      <li key={v} className="flex items-start gap-2.5 text-sm text-navy/70">
                        <Check size={16} className="mt-0.5 shrink-0 text-gold-dark" />
                        {v}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h4 className="text-xs font-semibold uppercase tracking-[0.25em] text-gold-dark">Il processo</h4>
                  <ol className="mt-4 space-y-3">
                    {service.processo.map((p, i) => (
                      <li key={p} className="flex items-start gap-3 text-sm text-navy/70">
                        <span className="font-display text-base italic leading-none text-gold">{String(i + 1).padStart(2, "0")}</span>
                        {p}
                      </li>
                    ))}
                  </ol>
                </div>
              </div>

              <Link
                to="/contatti"
                data-testid={`service-cta-${service.id}`}
                className="group/btn mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-6 py-3 text-sm font-semibold text-white transition-all duration-300 hover:bg-gold hover:text-navy"
              >
                Richiedi una consulenza
                <ArrowRight size={15} className="transition-transform duration-300 group-hover/btn:translate-x-1" />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {!open && (
        <span className="mt-auto inline-block pt-5 text-xs font-semibold uppercase tracking-[0.2em] text-navy/35 transition-colors duration-300 group-hover:text-gold-dark">
          Clicca per approfondire
        </span>
      )}
    </div>
  </Reveal>
);

const Servizi = () => {
  const [openId, setOpenId] = useState(null);

  return (
    <div data-testid="servizi-page">
      <PageHero
        eyebrow="I Nostri Servizi"
        lines={["Soluzioni integrate per l'intero", <span key="i" className="italic text-gold">ciclo di vita del credito</span>]}
        description="Dall'analisi dei portafogli fino al recupero e alla valorizzazione degli asset: un unico partner per banche, intermediari finanziari, investitori e imprese."
        image={heroImages.servizi}
      />

      <section className="bg-cream py-28" data-testid="servizi-grid-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading
            eyebrow="Cosa Facciamo"
            title="Otto aree di intervento, un unico interlocutore"
            description="Ogni servizio è parte di un percorso integrato: clicca sulle card per scoprire descrizione, vantaggi e processo di ciascuna area."
          />
          <div className="mt-16 grid items-stretch gap-6 md:grid-cols-2 lg:grid-cols-3">
            {services.map((s, i) => (
              <ServiceCard
                key={s.id}
                service={s}
                index={i}
                open={openId === s.id}
                onToggle={() => setOpenId(openId === s.id ? null : s.id)}
              />
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-28" data-testid="servizi-processo-section">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <SectionHeading eyebrow="Come Lavoriamo" title="Un metodo strutturato in quattro fasi" />
          <div className="mt-16 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {processSteps.map((p, i) => (
              <Reveal key={p.num} delay={i * 0.1}>
                <div data-testid={`servizi-process-${p.num}`} className="group h-full rounded-3xl border border-navy/10 bg-white p-8 transition-all duration-500 hover:-translate-y-2 hover:border-gold/50 hover:shadow-xl hover:shadow-navy/10">
                  <span className="font-display text-5xl italic text-gold/50 transition-colors duration-500 group-hover:text-gold">{p.num}</span>
                  <h3 className="mt-4 font-bold text-navy">{p.title}</h3>
                  <p className="mt-2.5 text-sm leading-relaxed text-navy/55">{p.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand title="Hai un portafoglio da valorizzare?" />
    </div>
  );
};

export default Servizi;
